import React, { useState, useEffect } from 'react';
import '../../styles/admin/AdminNews.css';

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    is_featured: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedNews, setSelectedNews] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [categories, setCategories] = useState([]);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  const statuses = [
    { value: '', label: 'Tất cả trạng thái' },
    { value: 'draft', label: 'Nháp' },
    { value: 'published', label: 'Đã xuất bản' },
    { value: 'archived', label: 'Lưu trữ' }
  ];

  useEffect(() => {
    fetchNews();
    fetchStatistics();
    fetchCategories();
  }, [currentPage, filters]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        const formattedCategories = [
          { slug: '', name: 'Tất cả danh mục' },
          ...data.data.map(cat => ({
            slug: cat.slug,
            name: cat.name
          }))
        ];
        setCategories(formattedCategories);
      } else {
        // Fallback to hardcoded categories if API fails
        setCategories([
          { slug: '', name: 'Tất cả danh mục' },
          { slug: 'company-news', name: 'Tin công ty' },
          { slug: 'technology', name: 'Công nghệ' },
          { slug: 'project-updates', name: 'Cập nhật dự án' },
          { slug: 'industry-news', name: 'Tin ngành' },
          { slug: 'events', name: 'Sự kiện' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to hardcoded categories
      setCategories([
        { slug: '', name: 'Tất cả danh mục' },
        { slug: 'company-news', name: 'Tin công ty' },
        { slug: 'technology', name: 'Công nghệ' },
        { slug: 'project-updates', name: 'Cập nhật dự án' },
        { slug: 'industry-news', name: 'Tin ngành' },
        { slug: 'events', name: 'Sự kiện' }
      ]);
    }
  };

  const fetchNews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...filters
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/news?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNews(data.data.news || []);
        setTotalPages(data.data.pagination?.totalPages || 1);
      } else {
        console.error('API returned error:', data.error);
        setNews([]);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/news/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStatistics(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSelectNews = (newsId) => {
    setSelectedNews(prev => {
      if (prev.includes(newsId)) {
        return prev.filter(id => id !== newsId);
      } else {
        return [...prev, newsId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedNews.length === news.length) {
      setSelectedNews([]);
    } else {
      setSelectedNews(news.map(n => n.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNews.length === 0) return;
    
    if (!window.confirm(`Bạn có chắc muốn xóa ${selectedNews.length} tin tức đã chọn?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/news/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedNews })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        alert(data.message);
        setSelectedNews([]);
        fetchNews();
        fetchStatistics();
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xóa tin tức này?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/news/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Xóa tin tức thành công!');
        fetchNews();
        fetchStatistics();
      } else {
        alert('Lỗi khi xóa tin tức');
      }
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleToggleFeatured = async (id, currentFeatured) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/news/${id}/featured`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_featured: !currentFeatured })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        fetchNews();
        fetchStatistics();
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert('Lỗi kết nối server');
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'draft': 'Nháp',
      'published': 'Đã xuất bản',
      'archived': 'Lưu trữ'
    };
    return statusMap[status] || status;
  };

  const getCategoryText = (category) => {
    const foundCategory = categories.find(cat => cat.slug === category);
    return foundCategory ? foundCategory.name : category;
  };

  return (
    <div className="admin-news">
      <div className="news-header">
        <div className="header-content">
          <h1>📰 Quản Lý Tin Tức</h1>
          <p>Quản lý tất cả tin tức của PhucAn Solutions</p>
          
          {/* Statistics Summary */}
          {statistics && (
            <div className="stats-summary">
              <div className="stat-item">
                <span className="stat-number">{statistics.total_news || 0}</span>
                <span className="stat-label">Tổng tin tức</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{statistics.published_news || 0}</span>
                <span className="stat-label">Đã xuất bản</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{statistics.featured_news || 0}</span>
                <span className="stat-label">Nổi bật</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{statistics.total_views || 0}</span>
                <span className="stat-label">Lượt xem</span>
              </div>
            </div>
          )}
        </div>
        <a href="/admin/news/create" className="create-btn">
          ➕ Tạo Tin Tức Mới
        </a>
      </div>

      {/* Filters */}
      <div className="news-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="🔍 Tìm kiếm tin tức..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="filter-select"
          >
            {categories.map(cat => (
              <option key={cat.slug} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.is_featured}
            onChange={(e) => handleFilterChange('is_featured', e.target.value)}
            className="filter-select"
          >
            <option value="">Tất cả</option>
            <option value="true">Nổi bật</option>
            <option value="false">Không nổi bật</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedNews.length > 0 && (
        <div className="bulk-actions">
          <div className="bulk-info">
            <span>Đã chọn {selectedNews.length} tin tức</span>
          </div>
          <div className="bulk-buttons">
            <button onClick={handleBulkDelete} className="bulk-delete-btn">
              🗑️ Xóa đã chọn
            </button>
            <button onClick={() => setSelectedNews([])} className="bulk-clear-btn">
              ✖️ Bỏ chọn tất cả
            </button>
          </div>
        </div>
      )}

      {/* News Table */}
      <div className="news-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner">🔄</div>
            <p>Đang tải tin tức...</p>
          </div>
        ) : (
          <table className="news-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={news.length > 0 && selectedNews.length === news.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Hình ảnh</th>
                <th>Tiêu đề</th>
                <th>Danh mục</th>
                <th>Trạng thái</th>
                <th>Tác giả</th>
                <th>Nổi bật</th>
                <th>Lượt xem</th>
                <th>Ngày tạo</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {news.length > 0 ? (
                news.map((newsItem) => (
                  <tr key={newsItem.id} className={selectedNews.includes(newsItem.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedNews.includes(newsItem.id)}
                        onChange={() => handleSelectNews(newsItem.id)}
                      />
                    </td>
                    <td>
                      <div className="news-image">
                        {newsItem.featured_image ? (
                          <img 
                            src={`${API_BASE_URL}${newsItem.featured_image}`}
                            alt={newsItem.title}
                          />
                        ) : (
                          <div className="no-image">📷</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="news-title">
                        <h4>{newsItem.title}</h4>
                        <p>{newsItem.excerpt?.substring(0, 100) || 'Không có mô tả'}...</p>
                      </div>
                    </td>
                    <td>
                      <span className="category-badge">
                        {getCategoryText(newsItem.category_name)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${newsItem.status}`}>
                        {getStatusText(newsItem.status)}
                      </span>
                    </td>
                    <td>
                      <span className="author">{newsItem.author_name}</span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleToggleFeatured(newsItem.id, newsItem.is_featured)}
                        className={`featured-btn ${newsItem.is_featured ? 'featured' : ''}`}
                        title={newsItem.is_featured ? 'Bỏ nổi bật' : 'Đánh dấu nổi bật'}
                      >
                        {newsItem.is_featured ? '⭐' : '☆'}
                      </button>
                    </td>
                    <td>
                      <span className="views">{newsItem.views_count || 0}</span>
                    </td>
                    <td>
                      <span className="date">
                        {new Date(newsItem.created_at).toLocaleDateString('vi-VN')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <a 
                          href={`/admin/news/edit/${newsItem.id}`}
                          className="edit-btn"
                          title="Chỉnh sửa"
                        >
                          ✏️
                        </a>
                        <button
                          onClick={() => handleDelete(newsItem.id)}
                          className="delete-btn"
                          title="Xóa"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="empty-state">
                    <div className="empty-content">
                      <p>📭 Không có tin tức nào phù hợp</p>
                      <a href="/admin/news/create" className="create-link">
                        Tạo tin tức đầu tiên
                      </a>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="page-btn"
          >
            ← Trước
          </button>
          
          <span className="page-info">
            Trang {currentPage} / {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Sau →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminNews; 